import React, { useCallback, useEffect, useState } from 'react';
import { App, Form, Image, Input, Modal, Upload } from 'antd';
import { listMyIssues, createIssue, uploadIssueAttachment } from '../../api/issues';
import { getApiErrorMessage } from '../../utils/apiError';
import TableCard from '../../components/TableCard';

const statutLabel = (v) => ({ OPEN: 'En attente', IN_PROGRESS: 'En cours', RESOLVED: 'Résolu' }[v] || v || '-');
const statutCls   = (v) => ({ OPEN: 'tag-orange', IN_PROGRESS: 'tag-blue', RESOLVED: 'tag-green' }[v] || 'tag-gray');

export default function Issues() {
  const { message, notification } = App.useApp();
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [videoObjectUrls, setVideoObjectUrls] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewIsVideo, setPreviewIsVideo] = useState(false);
  const [previewIsObjectUrl, setPreviewIsObjectUrl] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try { const data = await listMyIssues(); setRows(data); }
    catch (e) { notification.error({ message: getApiErrorMessage(e, 'Impossible de charger vos signalements.'), placement: 'topRight' }); }
    finally { setLoading(false); }
  }, [notification]);

  useEffect(() => { fetchAll(); const t = setInterval(() => fetchAll(), 10000); return () => clearInterval(t); }, [fetchAll]);
  useEffect(() => {
    const onRefresh = () => {
      fetchAll();
    };
    window.addEventListener('issues:refresh', onRefresh);
    return () => window.removeEventListener('issues:refresh', onRefresh);
  }, [fetchAll]);
  useEffect(() => () => { Object.values(videoObjectUrls || {}).forEach((u) => { try { URL.revokeObjectURL(u); } catch {} }); }, [videoObjectUrls]);

  const closePreview = () => { if (previewIsObjectUrl && previewUrl) { try { URL.revokeObjectURL(previewUrl); } catch {} } setPreviewOpen(false); setPreviewUrl(''); setPreviewIsVideo(false); setPreviewIsObjectUrl(false); };

  const onSubmit = async () => {
    try {
      const v = await form.validateFields();
      const created = await createIssue({ titre: v.titre, description: v.description });
      if (created?.id && fileList.length) { for (const f of fileList) { const raw = f?.originFileObj; if (!raw) continue; await uploadIssueAttachment(created.id, raw); } }
      message.success('Signalement envoyé avec succès.');
      setOpen(false); form.resetFields(); setFileList([]); fetchAll();
    } catch (e) { notification.error({ message: getApiErrorMessage(e, 'Envoi du signalement échoué.'), placement: 'topRight' }); }
  };

  return (
    <div className="page">
      <TableCard
        label="Mes signalements"
        loading={loading}
        rows={rows}
        toolbar={<button className="btn-primary" onClick={() => setOpen(true)}>+ Nouveau signalement</button>}
      >
        {(pageRows) => (
        <table>
          <thead>
            <tr>
              <th>Titre</th>
              <th>Description</th>
              <th>Envoyé le</th>
              <th>Résolu le</th>
              <th>Média</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => {
              const att = Array.isArray(r?.IssueReportAttachments) ? r.IssueReportAttachments : [];
              return (
                <tr key={r.id}>
                  <td>{r.titre || '-'}</td>
                  <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.description || '-'}</td>
                  <td>{r?.createdAt ? new Date(r.createdAt).toLocaleString() : '-'}</td>
                  <td>{r?.statut === 'RESOLVED' && r?.updatedAt ? new Date(r.updatedAt).toLocaleString() : '-'}</td>
                  <td>
                    {att.length === 0 ? '-' : (
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        {att.slice(0, 2).map((a) => {
                          const token = localStorage.getItem('access') || '';
                          const url = a?.path ? `${API_BASE}${a.path}?token=${token}` : null;
                          if (!url) return null;
                          if (String(a?.mime_type || '').startsWith('image/')) return <Image key={a.id} src={url} width={36} height={36} style={{ objectFit: 'cover', borderRadius: 4 }} />;
                          return <a key={a.id} href={url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#3b82f6' }}>Vidéo</a>;
                        })}
                        {att.length > 2 && <span style={{ fontSize: 10, color: '#aaa' }}>+{att.length - 2}</span>}
                      </div>
                    )}
                  </td>
                  <td><span className={`tag ${statutCls(r.statut)}`}>{statutLabel(r.statut)}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </TableCard>

      {/* New issue modal */}
      <Modal title="Nouveau signalement" open={open} onCancel={() => setOpen(false)} onOk={onSubmit} okText="Envoyer" okButtonProps={{ className: 'btn-primary' }}>
        <Form form={form} layout="vertical">
          <Form.Item name="titre" label="Titre" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}><Input.TextArea rows={4} /></Form.Item>
          <Form.Item label="Photo / vidéo (optionnel)">
            <Upload multiple accept="image/*,video/*" beforeUpload={() => false} listType="picture" fileList={fileList}
              onChange={({ fileList: fl }) => {
                fl.forEach((f) => {
                  const mt = String(f?.type || f?.originFileObj?.type || '');
                  if (mt.startsWith('image/') && f.originFileObj && !f.thumbUrl) { const reader = new FileReader(); reader.onload = () => { const url = String(reader.result || ''); setFileList((prev) => prev.map((p) => (p.uid === f.uid ? { ...p, thumbUrl: url } : p))); }; reader.readAsDataURL(f.originFileObj); }
                  if (mt.startsWith('video/') && f.originFileObj && !videoObjectUrls[f.uid]) { const u = URL.createObjectURL(f.originFileObj); setVideoObjectUrls((m) => ({ ...m, [f.uid]: u })); }
                });
                const nextUids = new Set(fl.map((f) => f.uid));
                Object.entries(videoObjectUrls).forEach(([uid, u]) => { if (!nextUids.has(uid)) { try { URL.revokeObjectURL(u); } catch {} setVideoObjectUrls((m) => { const copy = { ...m }; delete copy[uid]; return copy; }); } });
                setFileList(fl);
              }}
              onPreview={(file) => { const mt = String(file?.type || file?.originFileObj?.type || ''); const isVideo = mt.startsWith('video/'); const url = file?.thumbUrl || (file?.originFileObj ? URL.createObjectURL(file.originFileObj) : ''); setPreviewIsVideo(isVideo); setPreviewUrl(url); setPreviewIsObjectUrl(!file?.thumbUrl); setPreviewOpen(true); }}
              itemRender={(originNode, file) => { const mt = String(file?.type || file?.originFileObj?.type || ''); if (!mt.startsWith('video/')) return originNode; const u = videoObjectUrls[file.uid]; if (!u) return originNode; return <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><video src={u} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} muted playsInline /><div style={{ flex: 1, minWidth: 0 }}>{originNode}</div></div>; }}
            ><button className="btn-outline" type="button">Ajouter un fichier</button></Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* Preview modal */}
      <Modal title="Aperçu" open={previewOpen} onCancel={closePreview} footer={null} width={900}>
        {previewUrl && (previewIsVideo ? <video src={previewUrl} controls style={{ width: '100%' }} /> : <Image src={previewUrl} style={{ width: '100%' }} />)}
      </Modal>
    </div>
  );
}
